import {
    googleLoginEndpoint,
    jsonHeader,
    loginEndpoint,
    logoutEndpoint,
    resetPasswordEndpoint,
    sessionInfoEndpoint,
    signupEndpoint,
    userDetailsEndpoint
} from '../common/constants';
import { GoogleLoginParams, LoginParams, SignupParams, UserDetailsParams } from '../common/types';
import { SessionInfo } from '../pages/Credentials.types';

export const sessionInfo: () => Promise<SessionInfo> = async () => {
    const response = await fetch(sessionInfoEndpoint, {
        method: 'GET',
        headers: jsonHeader,
        credentials: 'include'
    });

    if (!response.ok) {
        const errorResponse = await response.json();

        throw new Error(errorResponse.message);
    }

    return await response.json();
};

export const updateUserDetails = async (userDetailsParams: UserDetailsParams) => {
    const response = await fetch(userDetailsEndpoint, {
        method: 'PUT',
        headers: jsonHeader,
        credentials: 'include',
        body: JSON.stringify(userDetailsParams)
    });

    if (!response.ok) {
        const errorResponse = await response.json();

        throw new Error(errorResponse.message);
    }

    return;
};

export const signup = async (signupParams: SignupParams) => {
    const response = await fetch(signupEndpoint, {
        method: 'POST',
        headers: jsonHeader,
        credentials: 'include',
        body: JSON.stringify(signupParams)
    });

    if (!response.ok) {
        const errorResponse = await response.json();

        throw new Error(errorResponse.message);
    }

    return await sessionInfo();
};

export const login = async (loginParams: LoginParams) => {
    const response = await fetch(loginEndpoint, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(loginParams),
        headers: jsonHeader
    });

    if (!response.ok) {
        const errorResponse = await response.json();

        throw new Error(errorResponse.message);
    }

    return await sessionInfo();
};

export const googleLogin = async (googleLoginParams: GoogleLoginParams) => {
    const { googleClientId, googleClickId, lang, isBusiness } = googleLoginParams;
    const urlSearchParams = new URLSearchParams();

    if (googleClientId != null) {
        urlSearchParams.set('googleId', `${googleClientId}`);
    }

    if (googleClickId != null) {
        urlSearchParams.set('gclid_field', `${googleClickId}`);
    }

    if (isBusiness != null) {
        urlSearchParams.set('isBusiness', `${isBusiness}`);
    }

    urlSearchParams.set('locale', lang);
    urlSearchParams.set('source', 'WEB');
    urlSearchParams.set('origin', `${window.location.origin}/`);

    const googleLoginUrl = `${googleLoginEndpoint}?${urlSearchParams.toString()}`;

    await googleLoginPopup(googleLoginUrl);
    const sessInfo = await sessionInfo();
    const twoMinutesInMS = 2 * 60 * 1000;

    return {
        ...sessInfo,
        justSignedUp: Date.now() - sessInfo.createdAt < twoMinutesInMS
    };
};

function googleLoginPopup(url: string) {
    return new Promise((resolve, reject) => {
        const win = window.open(url, 'windowname1', 'width=800, height=600');
        const interval = setInterval(() => {
            if (!win) {
                return console.warn('no window');
            }

            try {
                if (win.closed) {
                    clearInterval(interval);

                    return resolve(null);
                }

                if (win.document.URL.indexOf('userId') !== -1) {
                    clearInterval(interval);
                    const params = win.document.URL.split(/[?&]/).slice(1);

                    if (params.length) {
                        const pairs = params
                            .map((a) => a.split('='))
                            .reduce((acc, val) => {
                                acc[val[0]] = decodeURIComponent(val[1]);

                                return acc;
                            }, {});

                        resolve(pairs);
                    } else {
                        // should not be possible since we are looking for the userId in the url
                        reject(new Error('Impossible case: no parameters in redirect'));
                    }

                    win.close();
                }
            } catch (e) {
                // catch em all!
            }
        }, 100);
    });
}

export const logout = ((window as any).logout = async () => {
    const response = await fetch(logoutEndpoint, {
        method: 'POST',
        credentials: 'include'
    });

    if (!response.ok) {
        const errorResponse = await response.json();

        throw new Error(errorResponse.message);
    }

    return undefined;
});

export const requestResetPassword = async (email: string) => {
    const response = await fetch(resetPasswordEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: jsonHeader,
        body: JSON.stringify({ email })
    });

    if (!response.ok) {
        const errorResponse = await response.json();

        throw new Error(errorResponse.message);
    }

    return undefined;
};
